import { Modal } from 'components/Modals/Modal/Modal';
import { Field, Formik } from 'formik';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IVisibilityConfigurationDTO,
  useGetDealerPanelModelQuestions,
  useGetDocumentVisibilityOptions,
  useGetModelDocumentVisibility,
  useSubmitVisibilityConfiguration,
} from './hooks';
import Button from 'components/Button/Button';
import {
  ButtonsWrapper,
  Column,
  ErrorOcurredLabel,
  FormContainer,
  FormTitle,
  FormTitleWrapper,
  QuotationPricingSection,
  SectionLabel,
  TogglesRow,
  ToggleItem,
  HorizontalDivider,
  NoContentLabel,
  QuestionLabel,
  QuestionWrapper,
  Scrollable,
} from './ManageModelQuestionDocumentVisibilityModal.styled';
import CustomSelect from 'components/Select/FormikSelect';
import { IQuestion } from 'types/Question.types';
import Loader from 'components/Loader/Loader';
import {
  formatQuestionDisplayName,
  getInitialValuesFromQuestions,
  parseQuestionsObjectResponseIntoArray,
  SHOW_QUESTION_PRICES_ON_DOCUMENTS_INITIAL_KEY,
} from './helpers';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { getComponentsBasedOnDropdownType } from 'pages/ConfigurePage/ConfigureForm/FormItem/helpers';
import { DropdownType } from 'pages/ConfigurePage/ConfigureForm/FormItem/constants';
import { useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import Icon from 'components/Icon/Icon';
import Tooltip from 'components/Tooltip/Tooltip';
import { Info } from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import { IDocVisibilityOptionEnum } from './constants';

interface IManageModelQuestionDocumentVisibilityModalProps {
  manageVisibilityModelId: number | null;
  setManageVisibilityModelId: Dispatch<SetStateAction<number | null>>;
}

const ManageModelQuestionDocumentVisibilityModal = ({
  manageVisibilityModelId,
  setManageVisibilityModelId,
}: IManageModelQuestionDocumentVisibilityModalProps) => {
  const [areAllFieldsTouched, setAreAllFieldsTouched] =
    useState<boolean>(false);
  const { t } = useTranslation();
  const formikRef = useRef<any>(null);
  const base64Metadata: string | undefined = useSelector(
    (state: IRootReducerState) => state.submitConfigurationInfo.base64Metadata
  );

  const {
    data: documentVisibilityData,
    mutate: getModelDocumentVisibility,
    isLoading: isLoadingGetModelDocumentVisibility,
    reset: resetGetModelDocumentVisibility,
    isSuccess: isSuccessGetModelDocumentVisibility,
  } = useGetModelDocumentVisibility();

  const {
    data: questionsData,
    mutate: getDealerPanelModelQuestions,
    isLoading: isLoadingGetDealerPanelModelQuestions,
    reset: resetGetDealerPanelModelQuestions,
    isSuccess: isSuccessGetDealerPanelModelQuestions,
  } = useGetDealerPanelModelQuestions();

  // Regular and extra price questions are merged into `questions` ({ key: label }).
  const questions: IQuestion[] = questionsData?.questions
    ? parseQuestionsObjectResponseIntoArray(questionsData.questions)
    : [];

  useEffect(() => {
    if (manageVisibilityModelId) {
      getDealerPanelModelQuestions(manageVisibilityModelId);
      getModelDocumentVisibility(manageVisibilityModelId);
    } else {
      resetGetDealerPanelModelQuestions();
      resetGetModelDocumentVisibility();
    }
  }, [manageVisibilityModelId]);

  const {
    mutate: submitVisibilityConfiguration,
    isLoading: isLoadingSubmitVisibilityConfiguration,
  } = useSubmitVisibilityConfiguration();

  const handleSubmitVisibilityConfiguration = () => {
    const values = formikRef.current.values;
    const payload: IVisibilityConfigurationDTO = {
      values,
      modelId: manageVisibilityModelId!,
      base64Metadata,
    };
    submitVisibilityConfiguration(payload, {
      onSuccess: () => setManageVisibilityModelId(null),
    });
  };

  const documentVisibilityOptions = useGetDocumentVisibilityOptions();

  const modalHeight = questions.length * 50 + (questions.length - 1) * 30 + 200;

  const { isPhone } = useBreakpointFlag();

  return (
    <Modal
      isOpen={!!manageVisibilityModelId}
      onClose={() => {
        setManageVisibilityModelId(null);
      }}
      modalStyle={{
        position: 'fixed',
        margin: 'auto',
        width: '750rem',
        height: `${modalHeight}rem`,
        minHeight: '450rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      {isLoadingGetDealerPanelModelQuestions ||
      isLoadingGetModelDocumentVisibility ? (
        <Loader />
      ) : !questions.length ? (
        <NoContentLabel>
          {t('No questions found for this model')}
        </NoContentLabel>
      ) : isSuccessGetModelDocumentVisibility &&
        isSuccessGetDealerPanelModelQuestions ? (
        <>
          <Formik
            innerRef={formikRef}
            enableReinitialize
            onSubmit={() => {
              //
            }}
            initialValues={getInitialValuesFromQuestions(
              questions,
              documentVisibilityData,
              documentVisibilityOptions
            )}
          >
            {(formikProps: any) => {
              const { values, errors, setFieldValue } = formikProps;

              const showQuestionPricesOnDocumentsCurrentValue: IDocVisibilityOptionEnum[] =
                values[SHOW_QUESTION_PRICES_ON_DOCUMENTS_INITIAL_KEY] || [];

              return (
                <>
                  <FormTitleWrapper>
                    <FormTitle>
                      {t(
                        'Manage document visibility options for model questions'
                      )}
                    </FormTitle>
                  </FormTitleWrapper>
                  <QuotationPricingSection>
                    <SectionLabel>
                      {t('Quotation pricing visibility')}
                      <Tooltip
                        content={t(
                          'When enabled, extra costs (+) or discounts (-) for selected product options will appear on the quotation PDF.'
                        )}
                      >
                        <Icon svg={Info} size={20} color={COLORS.GREEN} />
                      </Tooltip>
                    </SectionLabel>
                    <TogglesRow>
                      <ToggleItem>
                        <QuestionLabel>
                          {t('Draft quotation PDF')}
                        </QuestionLabel>
                        <ToggleSwitch
                          size={isPhone ? 'small' : 'large'}
                          isChecked={showQuestionPricesOnDocumentsCurrentValue.includes(
                            IDocVisibilityOptionEnum.DRAFT_QUOTATION
                          )}
                          onClick={() => {
                            const currentValues =
                              showQuestionPricesOnDocumentsCurrentValue;
                            const newValues = currentValues.includes(
                              IDocVisibilityOptionEnum.DRAFT_QUOTATION
                            )
                              ? currentValues.filter(
                                  (v) =>
                                    v !==
                                    IDocVisibilityOptionEnum.DRAFT_QUOTATION
                                )
                              : [
                                  ...currentValues,
                                  IDocVisibilityOptionEnum.DRAFT_QUOTATION,
                                ];
                            setFieldValue(
                              SHOW_QUESTION_PRICES_ON_DOCUMENTS_INITIAL_KEY,
                              newValues
                            );
                          }}
                        />
                      </ToggleItem>
                      <ToggleItem>
                        <QuestionLabel>
                          {t('Definitive quotation PDF')}
                        </QuestionLabel>
                        <ToggleSwitch
                          size={isPhone ? 'small' : 'large'}
                          isChecked={showQuestionPricesOnDocumentsCurrentValue.includes(
                            IDocVisibilityOptionEnum.DEFINITIVE_QUOTATION
                          )}
                          onClick={() => {
                            const currentValues =
                              showQuestionPricesOnDocumentsCurrentValue;
                            const newValues = currentValues.includes(
                              IDocVisibilityOptionEnum.DEFINITIVE_QUOTATION
                            )
                              ? currentValues.filter(
                                  (v) =>
                                    v !==
                                    IDocVisibilityOptionEnum.DEFINITIVE_QUOTATION
                                )
                              : [
                                  ...currentValues,
                                  IDocVisibilityOptionEnum.DEFINITIVE_QUOTATION,
                                ];
                            setFieldValue(
                              SHOW_QUESTION_PRICES_ON_DOCUMENTS_INITIAL_KEY,
                              newValues
                            );
                          }}
                        />
                      </ToggleItem>
                    </TogglesRow>
                    <HorizontalDivider />
                  </QuotationPricingSection>
                  <Scrollable>
                    <FormContainer
                      height={`${
                        questions.length * 50 + (questions.length - 1) * 30
                      }rem`}
                    >
                      {questions.map((question: IQuestion, index: number) => (
                        <Column key={index}>
                          <QuestionLabel>
                            {formatQuestionDisplayName(question.name)}
                          </QuestionLabel>
                          <QuestionWrapper>
                            <Field
                              isMulti
                              width="100%"
                              name={question.initialKey}
                              options={documentVisibilityOptions}
                              components={getComponentsBasedOnDropdownType(
                                DropdownType.DEFAULT
                              )}
                              component={CustomSelect}
                              placeholder={formatQuestionDisplayName(
                                question.name
                              )}
                              onSelect={(value: string) =>
                                setFieldValue(question.initialKey, value)
                              }
                              errorMessage={
                                areAllFieldsTouched &&
                                errors[question.initialKey]
                              }
                              menuPlacement={index < 2 ? 'bottom' : 'top'}
                            />
                          </QuestionWrapper>
                        </Column>
                      ))}
                    </FormContainer>
                  </Scrollable>
                </>
              );
            }}
          </Formik>
          <ButtonsWrapper>
            <Button
              disabled={isLoadingSubmitVisibilityConfiguration}
              label={t('Cancel')}
              secondary
              width={`${isPhone ? '100rem' : '200rem'}`}
              onClick={() => setManageVisibilityModelId(null)}
            />
            <Button
              disabled={isLoadingSubmitVisibilityConfiguration}
              label={t('Save')}
              primary
              width={`${isPhone ? '100rem' : '200rem'}`}
              onClick={() => {
                if (formikRef.current?.isValid) {
                  handleSubmitVisibilityConfiguration();
                } else {
                  if (!areAllFieldsTouched) {
                    setAreAllFieldsTouched(true);
                  }
                }
              }}
            />
          </ButtonsWrapper>
        </>
      ) : (
        <ErrorOcurredLabel>{t('An error occurred')}</ErrorOcurredLabel>
      )}
    </Modal>
  );
};

export default ManageModelQuestionDocumentVisibilityModal;
